import {FormControl, Validators} from "@angular/forms";
import {ExamSession} from "../models/examSession";
import {Exam} from "../models/exam";
import {isValidPhone} from "../directives/phone-validator.directive";


export type RawRow = Record<string, string>;

export type ImportField = "firstname" | "lastname" | "email" | "phoneNumber" | "session" | "exam" | "psh" | "pshComment" | "fileNumber";

export type ImportRowStatus = "valid" | "incomplete" | "error" | "imported" | "warning" | "server_error";

export interface ImportRow {
    // Numéro de ligne dans le fichier (en-tête compris pour les tableurs)
    line: number;
    raw: RawRow;
    firstname: string;
    lastname: string;
    email: string;
    phoneNumber: string;
    sessionCode: string;
    voucherCode: string;
    psh: boolean;
    pshComment: string;
    fileNumber: string;
    session: ExamSession | null;
    exam: Exam | null;
    status: ImportRowStatus;
    messages: Array<string>;
}

export interface ImportValidationContext {
    sessionsByCode: Map<string, ExamSession>;
    seenEmails: Set<string>;
    seenVouchers: Set<string>;
}

export const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".ods", ".csv", ".json", ".xml"];

export const STATUS_LABELS: Record<ImportRowStatus, string> = {
    valid: "Valide",
    incomplete: "Incomplète",
    error: "Erreur",
    imported: "Importée",
    warning: "Non importée – avertissement",
    server_error: "Erreur serveur",
};

// Libellés des colonnes attendues, affichés à l'étape 1
export const EXPECTED_COLUMNS: Array<{label: string; type: string; required: boolean}> = [
    {label: "Prénom", type: "Texte (30 caractères max.)", required: true},
    {label: "Nom", type: "Texte (150 caractères max.)", required: true},
    {label: "Email", type: "Adresse email", required: true},
    {label: "Téléphone", type: "Numéro (06 12 34 56 78 ou +33 6 12 34 56 78)", required: true},
    {label: "Session", type: "Code de la session", required: true},
    {label: "Ticket", type: "Code du ticket, disponible dans la session", required: false},
    {label: "Handicap", type: "Oui / Non", required: false},
    {label: "Description du handicap", type: "Texte", required: false},
    {label: "Numéro de dossier CPF", type: "Texte", required: false},
];

const FIRSTNAME_MAX_LENGTH = 30;
const LASTNAME_MAX_LENGTH = 150;

const HEADER_ALIASES: Record<ImportField, Array<string>> = {
    firstname: ["prenom", "firstname"],
    lastname: ["nom", "lastname", "nomdefamille"],
    email: ["email", "mail", "courriel", "adresseemail", "adressemail"],
    phoneNumber: ["telephone", "tel", "phone", "phonenumber", "numerodetelephone", "portable"],
    session: ["session", "codesession", "sessioncode"],
    exam: ["ticket", "codeticket", "voucher", "vouchercode"],
    psh: ["handicap", "psh", "situationdehandicap", "candidatensituationdehandicap"],
    pshComment: ["descriptionhandicap", "descriptionduhandicap", "commentairehandicap", "pshcomment"],
    fileNumber: ["numerodossiercpf", "numerodedossiercpf", "dossiercpf", "filenumber"],
};

const ALIAS_TO_FIELD: Map<string, ImportField> = new Map(
    (Object.keys(HEADER_ALIASES) as Array<ImportField>).reduce(
        (acc, field) => acc.concat(HEADER_ALIASES[field].map((alias) => [alias, field] as [string, ImportField])),
        [] as Array<[string, ImportField]>
    )
);

const TRUE_VALUES = ["oui", "o", "yes", "y", "true", "vrai", "1", "x"];


export function normalizeHeader (header: string): string {
    return `${header}`
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
}

export function headerToField (header: string): ImportField | null {
    return ALIAS_TO_FIELD.get(normalizeHeader(header)) || null;
}

function cellToString (value: any): string {
    if (value == null) {
        return "";
    }
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (typeof value === "object") {
        return JSON.stringify(value);
    }
    return `${value}`.trim();
}

function isBlankRow (row: RawRow): boolean {
    return Object.keys(row).every((key) => row[key] === "");
}

function getExtension (filename: string): string {
    const index = filename.lastIndexOf(".");
    return index >= 0 ? filename.substring(index).toLowerCase() : "";
}

export function isAcceptedFile (file: File): boolean {
    return ACCEPTED_EXTENSIONS.includes(getExtension(file.name));
}


// ----------------------------------------------------------------------------
// Lecture du fichier
// ----------------------------------------------------------------------------

export interface ParsedFile {
    rows: Array<RawRow>;
    // Numéro de ligne dans le fichier, pour chaque élément de rows
    lines: Array<number>;
}

export async function parseFile (file: File): Promise<ParsedFile> {
    const extension = getExtension(file.name);
    let parsed: ParsedFile;

    switch (extension) {
        case ".xlsx":
        case ".xls":
        case ".ods":
            parsed = await parseSpreadsheet(await file.arrayBuffer(), false);
            break;
        case ".csv":
            parsed = await parseSpreadsheet(await readText(file), true);
            break;
        case ".json":
            parsed = parseJson(await readText(file));
            break;
        case ".xml":
            parsed = parseXml(await readText(file));
            break;
        default:
            throw new Error(`Format de fichier non supporté (${extension || "sans extension"}). Formats acceptés : ${ACCEPTED_EXTENSIONS.join(", ")}`);
    }

    if (parsed.rows.length === 0) {
        throw new Error("Le fichier ne contient aucune ligne de données.");
    }

    const headers = new Set<string>();
    parsed.rows.forEach((row) => Object.keys(row).forEach((key) => headers.add(key)));
    if (!Array.from(headers).some((header) => headerToField(header) != null)) {
        throw new Error("Aucune colonne reconnue dans le fichier. Vérifiez les en-têtes (Prénom, Nom, Email, Téléphone, Session…).");
    }

    return parsed;
}

// Les CSV exportés depuis Excel sont souvent en Windows-1252 : on tente l'UTF-8 strict d'abord
async function readText (file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    try {
        return new TextDecoder("utf-8", {fatal: true}).decode(buffer).replace(/^﻿/, "");
    } catch (e) {
        return new TextDecoder("windows-1252").decode(buffer);
    }
}

async function parseSpreadsheet (data: ArrayBuffer | string, isCsv: boolean): Promise<ParsedFile> {
    const XLSX = await import("xlsx");

    // raw: true pour les CSV afin de conserver les valeurs telles quelles (zéros initiaux des téléphones)
    const workbook = typeof data === "string"
        ? XLSX.read(data, {type: "string", raw: isCsv})
        : XLSX.read(data, {type: "array"});

    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    if (sheet == null) {
        return {rows: [], lines: []};
    }

    const records = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, {defval: "", raw: false});
    const rows: Array<RawRow> = [];
    const lines: Array<number> = [];

    records.forEach((record, index) => {
        const row = toRawRow(record);
        if (!isBlankRow(row)) {
            rows.push(row);
            // __rowNum__ est l'index (0-based) de la ligne dans la feuille, ajouté par SheetJS
            const rowNum = (record as any).__rowNum__;
            lines.push(rowNum != null ? rowNum + 1 : index + 2);
        }
    });

    return {rows, lines};
}

function toRawRow (record: Record<string, any>): RawRow {
    const row: RawRow = {};
    Object.keys(record).forEach((key) => {
        row[`${key}`.trim()] = cellToString(record[key]);
    });
    return row;
}

function parseJson (text: string): ParsedFile {
    let data: any;
    try {
        data = JSON.parse(text);
    } catch (e) {
        throw new Error("Le fichier JSON est invalide.");
    }

    // Accepte un tableau, ou un objet contenant un unique tableau (ex. {"preinscriptions": [...]})
    if (!Array.isArray(data) && data != null && typeof data === "object") {
        const arrays = Object.keys(data).filter((key) => Array.isArray(data[key]));
        if (arrays.length === 1) {
            data = data[arrays[0]];
        }
    }

    if (!Array.isArray(data)) {
        throw new Error("Le fichier JSON doit contenir un tableau d'objets (une pré-inscription par objet).");
    }

    return collectRows(
        data.filter((item) => item != null && typeof item === "object" && !Array.isArray(item))
            .map((item) => toRawRow(item))
    );
}

function parseXml (text: string): ParsedFile {
    const doc = new DOMParser().parseFromString(text, "application/xml");
    if (doc.getElementsByTagName("parsererror").length > 0) {
        throw new Error("Le fichier XML est invalide.");
    }

    // Chaque élément enfant de la racine est une pré-inscription,
    // ses sous-éléments et attributs sont les colonnes
    const records = Array.from(doc.documentElement.children);

    return collectRows(records.map((record) => {
        const row: RawRow = {};
        Array.from(record.attributes).forEach((attribute) => {
            row[attribute.name] = cellToString(attribute.value);
        });
        Array.from(record.children).forEach((child) => {
            row[child.tagName] = cellToString(child.textContent);
        });
        return row;
    }));
}

function collectRows (rows: Array<RawRow>): ParsedFile {
    const result: ParsedFile = {rows: [], lines: []};
    rows.forEach((row, index) => {
        if (!isBlankRow(row)) {
            result.rows.push(row);
            result.lines.push(index + 1);
        }
    });
    return result;
}


// ----------------------------------------------------------------------------
// Validation
// ----------------------------------------------------------------------------

export function createValidationContext (sessions: Array<ExamSession>): ImportValidationContext {
    const sessionsByCode = new Map<string, ExamSession>();
    (sessions || []).forEach((session) => sessionsByCode.set(`${session.code}`.trim().toLowerCase(), session));

    return {
        sessionsByCode,
        seenEmails: new Set<string>(),
        seenVouchers: new Set<string>(),
    };
}

function mapFields (raw: RawRow): Partial<Record<ImportField, string>> {
    const fields: Partial<Record<ImportField, string>> = {};
    Object.keys(raw).forEach((header) => {
        const field = headerToField(header);
        // En cas de colonnes en double, la première non vide l'emporte
        if (field != null && !fields[field]) {
            fields[field] = raw[header];
        }
    });
    return fields;
}

// Un tableur peut avoir converti un numéro français en nombre (06… → 6…)
function normalizePhone (value: string): string {
    return /^[1-9]\d{8}$/.test(value) ? `0${value}` : value;
}

export function validateRow (raw: RawRow, line: number, context: ImportValidationContext): ImportRow {
    const fields = mapFields(raw);
    const messages: Array<string> = [];
    const missing: Array<string> = [];

    const row: ImportRow = {
        line,
        raw,
        firstname: fields.firstname || "",
        lastname: fields.lastname || "",
        email: (fields.email || "").toLowerCase(),
        phoneNumber: normalizePhone(fields.phoneNumber || ""),
        sessionCode: fields.session || "",
        voucherCode: fields.exam || "",
        psh: TRUE_VALUES.includes((fields.psh || "").toLowerCase()),
        pshComment: fields.pshComment || "",
        fileNumber: fields.fileNumber || "",
        session: null,
        exam: null,
        status: "valid",
        messages,
    };

    if (!row.firstname) { missing.push("prénom"); }
    if (!row.lastname) { missing.push("nom"); }
    if (!row.email) { missing.push("email"); }
    if (!row.phoneNumber) { missing.push("téléphone"); }
    if (!row.sessionCode) { missing.push("session"); }

    if (missing.length > 0) {
        messages.push(`Champ(s) manquant(s) : ${missing.join(", ")}`);
    }

    let hasError = false;

    if (row.firstname.length > FIRSTNAME_MAX_LENGTH) {
        messages.push(`Le prénom dépasse ${FIRSTNAME_MAX_LENGTH} caractères`);
        hasError = true;
    }

    if (row.lastname.length > LASTNAME_MAX_LENGTH) {
        messages.push(`Le nom dépasse ${LASTNAME_MAX_LENGTH} caractères`);
        hasError = true;
    }

    if (row.email) {
        if (Validators.email(new FormControl(row.email)) != null) {
            messages.push("L'email est invalide");
            hasError = true;
        } else if (context.seenEmails.has(row.email)) {
            messages.push("Email en double dans le fichier");
            hasError = true;
        } else {
            context.seenEmails.add(row.email);
        }
    }

    if (row.phoneNumber && !isValidPhone(row.phoneNumber)) {
        messages.push("Le numéro de téléphone est invalide");
        hasError = true;
    }

    if (row.sessionCode) {
        row.session = context.sessionsByCode.get(row.sessionCode.toLowerCase()) || null;
        if (row.session == null) {
            messages.push(`Session « ${row.sessionCode} » introuvable ou non planifiée`);
            hasError = true;
        }
    }

    if (row.voucherCode && row.session != null) {
        const voucherKey = row.voucherCode.toLowerCase();
        const exam = (row.session.exams || []).find((e: Exam) => `${e.voucher_code}`.toLowerCase() === voucherKey);

        if (exam == null) {
            messages.push(`Ticket « ${row.voucherCode} » introuvable dans la session ${row.session.code}`);
            hasError = true;
        } else if (!exam.isAvailable) {
            messages.push(`Ticket « ${row.voucherCode} » déjà réservé`);
            hasError = true;
        } else if (context.seenVouchers.has(voucherKey)) {
            messages.push(`Ticket « ${row.voucherCode} » utilisé plusieurs fois dans le fichier`);
            hasError = true;
        } else {
            context.seenVouchers.add(voucherKey);
            row.exam = exam;
        }
    }

    if (hasError) {
        row.status = "error";
    } else if (missing.length > 0) {
        row.status = "incomplete";
    }

    return row;
}

export function validateRows (parsed: ParsedFile, sessions: Array<ExamSession>): Array<ImportRow> {
    const context = createValidationContext(sessions);
    return parsed.rows.map((raw, index) => validateRow(raw, parsed.lines[index], context));
}


// ----------------------------------------------------------------------------
// Rapport
// ----------------------------------------------------------------------------

export async function buildReport (rows: Array<ImportRow>): Promise<Blob> {
    const XLSX = await import("xlsx");

    const headers: Array<string> = ["Ligne"];
    rows.forEach((row) => Object.keys(row.raw).forEach((key) => {
        if (!headers.includes(key)) {
            headers.push(key);
        }
    }));
    headers.push("Statut", "Détail");

    const data = rows.map((row) => ({
        ...row.raw,
        "Ligne": row.line,
        "Statut": STATUS_LABELS[row.status],
        "Détail": row.messages.join(" ; "),
    }));

    const sheet = XLSX.utils.json_to_sheet(data, {header: headers});
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Rapport");

    const buffer = XLSX.write(workbook, {bookType: "xlsx", type: "array"});
    return new Blob([buffer], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
}
